// src/OfferFeedbackModal.js
import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';

function OfferFeedbackModal({ open, onClose, offer, onSubmit }) {
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setComment(''); // Reset comment when modal opens
            setError('');   // Reset error
        }
    }, [open]);

    const handleSubmit = async () => {
        if (!offer || !offer.offerid) {
            setError('Offer information is missing.');
            return;
        }
        setIsSubmitting(true);
        setError('');
        try {
            await onSubmit(offer.offerid, comment);
            onClose(); // Close on successful submission (parent will show snackbar)
        } catch (err) {
            const errMsg = err.response?.data?.message || err.response?.data?.title || err.message || 'Failed to submit feedback.';
            setError(errMsg);
            console.error("Feedback submission error:", err.response || err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!offer) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Report Issue with Offer</DialogTitle>
            <DialogContent>
                <Typography variant="h6" gutterBottom>
                    Offer: {offer.name_short || offer.name || 'Unnamed Task'}
                </Typography>
                <DialogContentText sx={{ mb: 2 }}>
                    Please describe the issue you encountered with this offer (e.g., did not credit, misleading instructions, technical problems). Your feedback helps us improve.
                    (Optional, max 400 characters)
                </DialogContentText>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <TextField
                    autoFocus
                    margin="dense"
                    id="feedback-comment"
                    label="Your Comment (Optional)"
                    type="text"
                    fullWidth
                    multiline
                    rows={4}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    disabled={isSubmitting}
                    inputProps={{ maxLength: 400 }}
                    helperText={`${comment.length}/400 characters`}
                />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default OfferFeedbackModal;